import { flow } from 'fp-ts/function';
import { createSelector } from 'reselect';
import { composeProps, createPropsExtender } from '@mews/framework';
import { compactArray, getStringProp } from '@mews/fp';
import { ApplicationState } from '@mews-commander/types';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { getEnableSkipCleaning } from '@mews-commander/legacy-entities/src/ducks/services';
import { getServiceOrderTimeUnitCount } from '@mews-commander/legacy-entities/src/selectors/orderItems';
import {
    getCurrentEnterpriseReservation,
    getCurrentEnterpriseReservationServiceId,
} from '@mews-commander/legacy-entities/src/selectors/reservations';
import { getResource } from '@mews-commander/legacy-entities/src/ducks/resources';
import { getHousekeepingStatusConditionFlags, HousekeepingOptionFlags } from './utils';

const getReservationSkipCleaningServiceOption = composeProps(
    flow(getEnableSkipCleaning, Boolean),
    createPropsExtender('id', getCurrentEnterpriseReservationServiceId)
);

const getReservationTimeUnitCount = composeProps(
    getServiceOrderTimeUnitCount,
    createPropsExtender('serviceOrderId', getStringProp('id'))
);
export const getReservationHousekeepingFlagSelector = createSelector(
    getCurrentEnterpriseReservation,
    getReservationSkipCleaningServiceOption,
    getReservationTimeUnitCount,
    (state, { resourceId }: { resourceId: string | undefined | null }) =>
        resourceId ? getResource(state, { id: resourceId, enterpriseId: getCurrentEnterpriseId() }) : null,
    (reservation, isSkipCleaningServiceOptionEnabled, timeUnitCount, resource) => {
        if (!reservation) return;

        const {
            reservationTimeState,
            options: { skipCleaning },
        } = reservation;
        return getHousekeepingStatusConditionFlags({
            reservationTimeState,
            timeUnitCount,
            isSkipCleaningServiceOptionEnabled,
            isSkipAllCleaningReservationOptionEnabled: skipCleaning,
            resource,
        });
    }
);
export const getReservationsAggregatedHousekeepingFlags = (
    state: ApplicationState,
    { reservationIds }: { reservationIds: string[] }
): HousekeepingOptionFlags => {
    const individualReservationFlags = compactArray(
        reservationIds.map((id) => {
            const reservation = getCurrentEnterpriseReservation(state, { id });
            return getReservationHousekeepingFlagSelector(state, { id, resourceId: reservation?.assignedResourceId });
        })
    );

    return {
        makeSpaceInspected: individualReservationFlags.some((flags) => flags.makeSpaceInspected),
        skipAllCleaning: individualReservationFlags.every((flags) => flags.skipAllCleaning),
        skipTodayCleaning: individualReservationFlags.every((flags) => flags.skipTodayCleaning),
        enableCleaning: individualReservationFlags.every((flags) => flags.enableCleaning),
    };
};
